'use client';

import { useState } from 'react';

async function copyText(value: string) {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(value);
    return;
  }
  const textarea = document.createElement('textarea');
  textarea.value = value;
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'absolute';
  textarea.style.left = '-9999px';
  document.body.append(textarea);
  textarea.select();
  document.execCommand('copy');
  textarea.remove();
}

type FillOrder = 'row' | 'column';
type OutputFormat = 'aligned' | 'table';

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

// Arrange items into a grid of `columns` columns.
function buildGrid(items: string[], columns: number, order: FillOrder): string[][] {
  const rowCount = Math.ceil(items.length / columns);
  const grid: string[][] = Array.from({ length: rowCount }, () =>
    Array.from({ length: columns }, () => ''),
  );

  if (order === 'row') {
    items.forEach((item, index) => {
      const r = Math.floor(index / columns);
      const c = index % columns;
      grid[r][c] = item;
    });
  } else {
    items.forEach((item, index) => {
      const c = Math.floor(index / rowCount);
      const r = index % rowCount;
      if (grid[r]) {
        grid[r][c] = item;
      }
    });
  }
  return grid;
}

function renderAligned(grid: string[][], columns: number, separator: string): string {
  const widths: number[] = Array.from({ length: columns }, () => 0);
  for (const row of grid) {
    for (let c = 0; c < columns; c += 1) {
      widths[c] = Math.max(widths[c], (row[c] ?? '').length);
    }
  }
  return grid
    .map((row) => {
      const cells = row.map((cell, c) => {
        // Do not pad the final column so trailing whitespace stays clean.
        if (c === columns - 1) {
          return cell ?? '';
        }
        return (cell ?? '').padEnd(widths[c], ' ');
      });
      return cells.join(separator).replace(/\s+$/, '');
    })
    .join('\n');
}

function renderTable(grid: string[][]): string {
  const rows = grid
    .map((row) => {
      const cells = row.map((cell) => `    <td>${escapeHtml(cell ?? '')}</td>`).join('\n');
      return `  <tr>\n${cells}\n  </tr>`;
    })
    .join('\n');
  return `<table>\n${rows}\n</table>`;
}

export default function BrowserListToColumns() {
  const [input, setInput] = useState('');
  const [columns, setColumns] = useState(3);
  const [order, setOrder] = useState<FillOrder>('row');
  const [format, setFormat] = useState<OutputFormat>('aligned');
  const [separator, setSeparator] = useState('   ');
  const [trimBlanks, setTrimBlanks] = useState(true);
  const [output, setOutput] = useState('');
  const [copyMessage, setCopyMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  function handleConvert() {
    setCopyMessage(null);
    if (!input.trim()) {
      setErrorMessage('Paste a list (one item per line) first.');
      setOutput('');
      return;
    }
    const safeColumns = Math.max(1, Math.min(50, Math.floor(columns) || 0));
    setColumns(safeColumns);

    let items = input.split(/\r?\n/);
    if (trimBlanks) {
      items = items.filter((item) => item.trim().length > 0);
    }
    if (items.length === 0) {
      setErrorMessage('No items left after removing blank lines.');
      setOutput('');
      return;
    }
    setErrorMessage(null);

    const grid = buildGrid(items, safeColumns, order);
    const resolvedSeparator = separator === '\\t' ? '\t' : separator;
    const sep = resolvedSeparator.length > 0 ? resolvedSeparator : '   ';
    setOutput(format === 'table' ? renderTable(grid) : renderAligned(grid, safeColumns, sep));
  }

  async function handleCopy() {
    if (!output) {
      return;
    }
    try {
      await copyText(output);
      setCopyMessage('Copied to clipboard.');
    } catch {
      setCopyMessage('The browser could not copy the output.');
    }
  }

  return (
    <div className="tool-live-shell">
      <div className="tool-live-header">
        <span className="tool-live-badge">List to columns</span>
        <h2 className="tool-live-title">Turn a list into aligned columns</h2>
        <p className="tool-live-subtitle">
          Paste one item per line and lay it out into aligned monospace columns or an HTML table,
          filled by row or by column. Runs fully in your browser.
        </p>
      </div>

      <div className="tool-live-grid">
        <section className="tool-live-uploader">
          <label className="tool-live-field tool-live-field-full">
            <span>Input list</span>
            <textarea
              onChange={(e) => setInput(e.target.value)}
              placeholder={'One item per line…\nApple\nBanana\nCherry\nDate'}
              rows={12}
              value={input}
            />
          </label>
          <div className="tool-live-controls">
            <label className="tool-live-field">
              <span>Columns</span>
              <input
                min="1"
                max="50"
                onChange={(e) => setColumns(Number(e.target.value))}
                type="number"
                value={columns}
              />
            </label>
            <label className="tool-live-field">
              <span>Fill order</span>
              <select onChange={(e) => setOrder(e.target.value as FillOrder)} value={order}>
                <option value="row">By row (left to right)</option>
                <option value="column">By column (top to bottom)</option>
              </select>
            </label>
            <label className="tool-live-field">
              <span>Output</span>
              <select onChange={(e) => setFormat(e.target.value as OutputFormat)} value={format}>
                <option value="aligned">Aligned text</option>
                <option value="table">HTML table</option>
              </select>
            </label>
            {format === 'aligned' ? (
              <label className="tool-live-field">
                <span>Column separator</span>
                <select onChange={(e) => setSeparator(e.target.value)} value={separator}>
                  <option value="   ">3 spaces</option>
                  <option value="  ">2 spaces</option>
                  <option value=" ">1 space</option>
                  <option value="\t">Tab</option>
                  <option value=" | ">Pipe ( | )</option>
                  <option value=" , ">Comma ( , )</option>
                </select>
              </label>
            ) : null}
            <label className="tool-live-field">
              <span>Trim blank lines</span>
              <select
                onChange={(e) => setTrimBlanks(e.target.value === 'yes')}
                value={trimBlanks ? 'yes' : 'no'}
              >
                <option value="yes">Yes</option>
                <option value="no">No</option>
              </select>
            </label>
          </div>
          <div className="tool-live-actions">
            <button className="button button-secondary" onClick={handleConvert} type="button">
              Convert
            </button>
            <button
              className="button button-secondary"
              disabled={!output}
              onClick={handleCopy}
              type="button"
            >
              Copy result
            </button>
          </div>
          {errorMessage ? (
            <p className="status-message" style={{ color: '#dc2626' }}>
              {errorMessage}
            </p>
          ) : null}
          {copyMessage ? <p className="status-message status-success">{copyMessage}</p> : null}
        </section>

        <section className="tool-live-preview">
          <label className="tool-live-field tool-live-field-full">
            <span>Columns output</span>
            <textarea
              placeholder="Press Convert to lay your list out into columns."
              readOnly
              rows={16}
              style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' }}
              value={output}
            />
          </label>
          <p className="tool-live-panel-note">
            Generated locally in your browser. Nothing is uploaded.
          </p>
        </section>
      </div>
    </div>
  );
}
